import * as React from 'react'

import { GripVerticalIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import MarkupContextToolbar from './MarkupContextToolbar'
import MarkupEditorStore, { TOOLBAR_GAP } from './MarkupEditorStore'
import { MarkupPortalContainerContext } from './MarkupPortalContainer'
import MarkupSelectionChrome from './MarkupSelectionChrome'
import MarkupSubToolbar from './MarkupSubToolbar'
import MarkupTextContextToolbar from './MarkupTextContextToolbar'
import MarkupToolbar from './MarkupToolbar'

interface IProps {
  store: MarkupEditorStore
  portalContainer?: HTMLElement | null
  // While the workflow form is minimized, the toolbar becomes a single "common wrapper"
  // cluster: a leading drag grip + the tools + this trailing "Open workflow" action.
  // Both are supplied by the composer; omitted (no wrapper) when the form is expanded.
  dockedTrailing?: React.ReactNode
  onDockedGripDown?: (e: React.PointerEvent) => void
}

const DesktopMarkupEditor: React.FC<IProps> = observer(
  ({ store, portalContainer, dockedTrailing, onDockedGripDown }) => {
    React.useEffect(() => {
      store.maybeShowLegendHint()
    }, [store])

    return (
      <MarkupPortalContainerContext.Provider value={portalContainer ?? null}>
        <div
          ref={store.overlayRef}
          className="tw:pointer-events-none tw:absolute tw:inset-0 tw:z-10"
        >
          <MarkupSelectionChrome store={store} />
          {/* Location-change mode hides the whole toolbar; the parent shows
                Cancel/Done instead (Workflows-map pill / Precise Location footer). */}
          {!store.isLocationEditActive && (
            // Positioned by the composer's --markup-toolbar-* vars: docked under the
            // MapForm card while expanded, else bottom-center (top wins via bottom:auto).
            // data-docked-cluster lets the composer's grip drag this whole cluster.
            <div
              data-docked-cluster
              className="tw:pointer-events-auto tw:absolute tw:top-auto tw:bottom-[var(--markup-toolbar-bottom,0.75rem)] tw:left-[var(--markup-toolbar-center,50%)] tw:flex tw:-translate-x-1/2 tw:flex-col tw:items-center tw:gap-2"
            >
              <MarkupSubToolbar
                isMobileEnv={false}
                activeTool={store.activeTool}
                color={store.color}
                strokeWidth={store.strokeWidth}
                shapeKind={store.shapeKind}
                lineType={store.lineType}
                drawType={store.drawType}
                fillColor={store.fillColor}
                fillStyle={store.fillStyle}
                onColorChange={store.changeColor}
                onStrokeWidthChange={store.changeStrokeWidth}
                onShapeKindChange={store.changeShapeKind}
                onLineTypeChange={store.changeLineType}
                onDrawTypeChange={store.changeDrawType}
                onResetToSelect={() => store.selectTool('select')}
                onFillColorChange={store.changeFillColor}
                onFillStyleChange={store.changeFillStyle}
                polygonDrawing={store.polygonDrawing}
                onFinishPolygon={store.finishPolygon}
                onCancelPolygon={store.cancelPolygon}
                polylineDrawing={store.polylineDrawing}
                onFinishPolyline={store.finishPolyline}
                onCancelPolyline={store.cancelPolyline}
              />
              <div
                className={
                  dockedTrailing
                    ? 'tw:flex tw:items-center tw:gap-2 tw:rounded-2xl tw:border tw:border-border tw:bg-background/80 tw:p-1.5 tw:shadow-lg'
                    : 'tw:flex tw:items-start tw:gap-2'
                }
              >
                {dockedTrailing && onDockedGripDown && (
                  <button
                    type="button"
                    draggable={false}
                    onDragStart={e => e.preventDefault()}
                    onPointerDown={onDockedGripDown}
                    aria-label={Localization.translator.move}
                    className="tw:flex tw:shrink-0 tw:cursor-grab tw:touch-none tw:appearance-none tw:items-center tw:border-0 tw:bg-transparent tw:px-1 tw:text-muted-foreground tw:outline-none"
                  >
                    <GripVerticalIcon
                      className="tw:pointer-events-none tw:size-4"
                      aria-hidden="true"
                    />
                  </button>
                )}
                <MarkupToolbar
                  isMobileEnv={false}
                  activeTool={store.activeTool}
                  canUndo={store.canUndo}
                  canRedo={store.canRedo}
                  legendOpen={store.showLegend}
                  showLegendHint={store.showLegendHint}
                  onSelectTool={store.selectTool}
                  onUndo={store.undo}
                  onRedo={store.redo}
                  onLegendOpenChange={store.setLegendOpen}
                  showLocationTool={store.showLocationTool}
                  isLocationEditActive={store.isLocationEditActive}
                  onEditPreciseLocation={store.onEditPreciseLocation}
                  legendExtraGroups={store.legendExtraGroups}
                />
                {dockedTrailing}
              </div>
            </div>
          )}
          {renderContextToolbar(store)}
          {renderTextToolbar(store)}
        </div>
      </MarkupPortalContainerContext.Provider>
    )
  },
)

// On desktop the context (Properties) toolbar floats and can be dragged to pin.
function renderContextToolbar(store: MarkupEditorStore): JSX.Element {
  if (!store.selection || !store.selectionScreen || store.interacting)
    return null
  const pos = store.pinnedToolbarPos ?? store.selectionScreen
  return (
    <div
      ref={store.toolbarRef}
      className="tw:pointer-events-auto tw:absolute tw:z-20"
      style={{
        left: pos.x,
        top: pos.y,
        transform: `translate(-50%, calc(-100% - ${TOOLBAR_GAP}px))`,
      }}
    >
      <MarkupContextToolbar
        kind={store.selection.kind}
        color={store.selection.color}
        strokeWidth={store.selection.strokeWidth}
        strokeStyle={store.selection.strokeStyle}
        fillColor={store.selection.fillColor}
        fillStyle={store.selection.fillStyle}
        arrowheadStart={store.selection.arrowheadStart}
        arrowheadEnd={store.selection.arrowheadEnd}
        isMobileEnv={false}
        onColorChange={store.changeSelectedColor}
        onStrokeWidthChange={store.changeSelectedWidth}
        onStrokeStyleChange={store.changeSelectedStrokeStyle}
        onFillColorChange={store.changeSelectedFillColor}
        onFillStyleChange={store.changeSelectedFillStyle}
        onArrowheadChange={store.changeSelectedArrowhead}
        onMovePointerDown={store.startToolbarDrag}
        onDelete={store.deleteSelectedMarkup}
      />
    </div>
  )
}

function renderTextToolbar(store: MarkupEditorStore): JSX.Element {
  const box = store.textStore.selected
  const screen = box && store.textScreens.get(box.id)
  if (!box || !screen) return null
  const anchor =
    store.pinnedTextToolbarPos ?? store.textToolbarAnchor(box.id) ?? screen
  return (
    <div
      ref={store.textToolbarRef}
      data-markup-text-toolbar
      onPointerDownCapture={() => store.markTextToolbarInteraction()}
      className="tw:pointer-events-auto tw:absolute tw:z-20"
      style={{
        left: anchor.x,
        top: anchor.y,
        transform: `translate(-50%, calc(-100% - ${TOOLBAR_GAP}px))`,
      }}
    >
      <MarkupTextContextToolbar
        color={box.color}
        fillColor={box.fillColor}
        fontSize={box.fontSize}
        bold={box.bold}
        italic={box.italic}
        underline={box.underline}
        isMobileEnv={false}
        onColorChange={store.changeTextColor}
        onFillColorChange={store.changeTextFillColor}
        onFontSizeChange={store.changeTextFontSize}
        onToggleBold={store.toggleTextBold}
        onToggleItalic={store.toggleTextItalic}
        onToggleUnderline={store.toggleTextUnderline}
        onDelete={store.deleteSelectedText}
        onMovePointerDown={store.startTextToolbarDrag}
      />
    </div>
  )
}

export default DesktopMarkupEditor
